import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { getProfitability, getPipelines } from '../actions';
import type { ProfitabilityQuery } from '../actions';
import type { JobProfitability, Pipeline, PaginationMeta } from '../types';
import {
  formatCurrency,
  formatMargin,
  formatSignedCurrency,
  formatSignedPercent,
  buildGhlOpportunityUrl,
} from '../format';
import { Pagination } from '../Pagination';
import { OverviewChart } from './OverviewChart';
import classes from '../jobCosting.module.css';

interface Props {
  ssoToken: string;
  locationId: string;
  onSelectJob: (opportunityId: string, name: string | null) => void;
}

// GHL opportunity status values used for the filter dropdown.
const STATUS_OPTIONS = ['open', 'won', 'lost', 'abandoned'];

const EMPTY_META: PaginationMeta = { currentPage: 1, lastPage: 1, perPage: 50, total: 0 };

// Variance cell colour without nesting ternaries (eslint no-nested-ternary).
const varianceClass = (hasBudget: boolean, variance: number): string => {
  if (!hasBudget) return '';
  return variance >= 0 ? classes.profitPositive : classes.profitNegative;
};

type SortField = 'monetary_value' | 'created_at' | 'updated_at';

function JobCostingProfitability({ ssoToken, locationId, onSelectJob }: Props) {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [status, setStatus] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<JobProfitability[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    getPipelines(ssoToken)
      .then((p) => {
        if (mounted) setPipelines(p);
      })
      .catch(() => {
        /* filter dropdown is non-critical */
      });
    return () => {
      mounted = false;
    };
  }, [ssoToken]);

  const stages = useMemo(
    () => pipelines.find((p) => p.id === pipelineId)?.stages ?? [],
    [pipelines, pipelineId]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: ProfitabilityQuery = {
        pipelineId: pipelineId || undefined,
        pipelineStageId: stageId || undefined,
        status: status || undefined,
        sort: `${sortDir === 'desc' ? '-' : ''}${sortField}`,
        page,
      };
      const result = await getProfitability(ssoToken, query);
      setRows(result.data);
      setMeta(result.meta);
    } catch {
      setError('Unable to load profitability. Please try again.');
      setRows([]);
      setMeta(EMPTY_META);
    } finally {
      setLoading(false);
    }
  }, [ssoToken, pipelineId, stageId, status, sortField, sortDir, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = useCallback((field: SortField) => {
    setPage(1);
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('desc');
      return field;
    });
  }, []);

  // Portfolio totals for the current page of results.
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.revenue += r.revenue;
          acc.cost += r.totalCost;
          acc.profit += r.grossProfit;
          if (r.hasBudget) {
            acc.budget += r.totalBudget;
            acc.variance += r.budgetVariance;
            acc.anyBudget = true;
          }
          return acc;
        },
        { revenue: 0, cost: 0, profit: 0, budget: 0, variance: 0, anyBudget: false }
      ),
    [rows]
  );

  const onFilterChange = (setter: (v: string) => void) => (value: string) => {
    setPage(1);
    setter(value);
  };

  let revenueSortIcon = '';
  if (sortField === 'monetary_value') revenueSortIcon = sortDir === 'asc' ? '↑' : '↓';

  const renderBody = () => {
    if (loading) {
      return (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profitability...</p>
        </div>
      );
    }
    if (rows.length === 0) {
      return <div className={classes.emptyState}>No jobs found for the selected filters.</div>;
    }
    return (
      <>
        <OverviewChart rows={rows} />
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Job</th>
                <th className={classes.num} onClick={() => handleSort('monetary_value')}>
                  {`Revenue ${revenueSortIcon}`}
                </th>
                <th className={classes.num}>Total Cost</th>
                <th className={classes.num}>Gross Profit</th>
                <th className={classes.num}>Margin</th>
                <th className={classes.num}>Budget</th>
                <th className={classes.num}>Variance</th>
                <th className={classes.num}>Var %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.opportunityId}
                  className={classes.rowClickable}
                  onClick={() => onSelectJob(r.opportunityId, r.name)}
                >
                  <td>
                    <div className={classes.jobPrimary}>
                      {r.rpJobNumber && (
                        <span className={classes.jobNumber}>{`#${r.rpJobNumber} · `}</span>
                      )}
                      {r.name || 'Untitled'}
                      <a
                        href={buildGhlOpportunityUrl(locationId, r.opportunityId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.externalLink}
                        title="Open opportunity in RocketCRM"
                        onClick={(e) => e.stopPropagation()}
                      >
                        ↗
                      </a>
                    </div>
                    {r.address && <div className={classes.jobAddress}>{r.address}</div>}
                  </td>
                  <td className={classes.num}>{formatCurrency(r.revenue)}</td>
                  <td className={classes.num}>{formatCurrency(r.totalCost)}</td>
                  <td
                    className={`${classes.num} ${r.grossProfit >= 0 ? classes.profitPositive : classes.profitNegative}`}
                  >
                    {formatCurrency(r.grossProfit)}
                  </td>
                  <td className={classes.num}>{formatMargin(r.grossMarginPct)}</td>
                  <td className={classes.num}>{r.hasBudget ? formatCurrency(r.totalBudget) : '—'}</td>
                  <td className={`${classes.num} ${varianceClass(r.hasBudget, r.budgetVariance)}`}>
                    {r.hasBudget ? formatSignedCurrency(r.budgetVariance) : '—'}
                  </td>
                  <td className={`${classes.num} ${varianceClass(r.hasBudget, r.budgetVariance)}`}>
                    {r.hasBudget ? formatSignedPercent(r.budgetVariancePct) : '—'}
                  </td>
                  <td>{r.status || '—'}</td>
                </tr>
              ))}
              <tr className={classes.totalsRow}>
                <td>Totals (page)</td>
                <td className={classes.num}>{formatCurrency(totals.revenue)}</td>
                <td className={classes.num}>{formatCurrency(totals.cost)}</td>
                <td className={classes.num}>{formatCurrency(totals.profit)}</td>
                <td className={classes.num}>—</td>
                <td className={classes.num}>
                  {totals.anyBudget ? formatCurrency(totals.budget) : '—'}
                </td>
                <td className={classes.num}>
                  {totals.anyBudget ? formatSignedCurrency(totals.variance) : '—'}
                </td>
                <td className={classes.num}>—</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Pagination meta={meta} onPageChange={setPage} />
      </>
    );
  };

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Pipeline</span>
          <select
            className={classes.select}
            value={pipelineId}
            onChange={(e) => {
              onFilterChange(setPipelineId)(e.target.value);
              setStageId('');
            }}
          >
            <option value="">All pipelines</option>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className={classes.field}>
          <span className={classes.fieldLabel}>Stage</span>
          <select
            className={classes.select}
            value={stageId}
            onChange={(e) => onFilterChange(setStageId)(e.target.value)}
            disabled={!pipelineId}
          >
            <option value="">All stages</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className={classes.field}>
          <span className={classes.fieldLabel}>Status</span>
          <select
            className={classes.select}
            value={status}
            onChange={(e) => onFilterChange(setStatus)(e.target.value)}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={`alert alert-danger ${classes.emptyState}`}>{error}</div>}

      {renderBody()}
    </div>
  );
}

const JobCostingProfitabilityMemo = memo(JobCostingProfitability, areEqual);

export { JobCostingProfitabilityMemo as JobCostingProfitability };
